/**
 * SmartShuffle v2 — Gradual Drift Product Rotation Engine
 *
 * Behavior:
 *   - Each F5 swaps ~25% of displayed products (gradual, not jarring)
 *   - After ~10 refreshes, ~94% of products have rotated out
 *   - L3 category composition drifts naturally over the rotation window
 *   - New products are chosen via weighted scoring (freshness, affinity, exploration)
 *   - Diversity constraints prevent vendor/category monopolies
 *   - Backward-compatible: supports static/daily/weekly modes
 */
(() => {
  'use strict';

  // ═══════════════════════════════════════════════════════
  // §1  Constants & Storage Keys
  // ═══════════════════════════════════════════════════════

  const CACHE_VERSION = 7;  // Bump to invalidate stale homepage metadata cache
  const SIGNALS_KEY = 'zh_user_signals';
  const MEMORY_KEY = 'zh_shuffle_memory';
  const DISPLAY_KEY = 'zh_current_display';   // tracks what's currently shown
  const HISTORY_KEY = 'zh_viewed_products';
  const CAT_HISTORY_KEY = 'zh_cat_exposure';     // L3 category exposure tracker
  const MAX_HISTORY = 20;
  const MAX_MEMORY_VISITS = 10;

  // Invalidate stale caches when version changes
  (function checkCacheVersion() {
    const stored = lsGet('zh_cache_version', 0);
    if (stored < CACHE_VERSION) {
      try {
        localStorage.removeItem(DISPLAY_KEY + '_home_next');
        localStorage.removeItem(DISPLAY_KEY + '_home_next_html');
        localStorage.removeItem(DISPLAY_KEY + '_home_next_meta');
        localStorage.removeItem(DISPLAY_KEY + '_home');
      } catch { }
      lsSet('zh_cache_version', CACHE_VERSION);
    }
  })();

  // ── Drift tuning ──
  const SWAP_RATE = 0.40;   // swap 40% per refresh → 7 visits ≈ 97% turnover (0.6^7=2.8%)
  const CAT_WINDOW = 7;      // track category exposure over last N visits

  // Scoring weights
  const W = {
    base: 1.0,
    freshness30d: 0.3,
    freshness90d: 0.15,
    affinityVendor: 0.15,
    affinityCategory: 0.10,
    imageBonus: 0.1,
    exploration: 0.15,
    // Category distance bonuses ×1.5
    catNewL1: 0.75,     // product from L1 never shown recently → massive bonus
    catNewL2: 0.45,     // product from L2 never shown recently
    catNewL3: 0.22,     // product from L3 never shown recently
    // Category over-exposure penalty ×1.5 more aggressive
    catOverexposedL1: 0.10,    // L1 branch shown > threshold → 90% weight cut
    catOverexposedL2: 0.20,    // L2 shown > threshold → 80% weight cut
    fatigueMul: [0.01, 0.03, 0.2],
  };

  // ── Partner vendors: guaranteed 50% of display slots ──
  const PARTNER_VENDORS = [
    'NIPPON BEARING',
    'ASA DENSHI 麻電子工業',
    'TOYOX 東洋克斯',
    'NBK',
    'SMT 南海精工所',
    'CONSUSS',
    'YURA 由良產商',
    'PASCAL 帕斯卡',
  ];
  const PARTNER_SET = new Set(PARTNER_VENDORS.map(v => v.toUpperCase()));
  const PARTNER_RATIO = 0.50;  // 50% of slots reserved for partners

  function isPartner(vendor) {
    return PARTNER_SET.has((vendor || '').toUpperCase());
  }

  // PASCAL is the top-tier partner — extra weight, especially nitrogen gas springs
  function isPascal(vendor) {
    return (vendor || '').toUpperCase().includes('PASCAL');
  }
  // Detect PASCAL nitrogen gas spring main products (not accessories/parts)
  const GAS_SPRING_KEYWORDS = ['氮氣彈簧', 'ガススプリング', 'gas spring', 'nitrogen', 'KS', 'KP', 'KA'];
  const ACCESSORY_KEYWORDS = ['配件', '部品', '附件', 'accessory', 'parts', 'repair', 'seal', 'adapter', 'bracket', 'holder'];
  function isPascalGasSpring(product) {
    if (!isPascal(product.vendor)) return false;
    const text = (product.tags.join(' ') + ' ' + (product.type || '') + ' ' + (product.handle || '')).toUpperCase();
    const isGasSpring = GAS_SPRING_KEYWORDS.some(kw => text.includes(kw.toUpperCase()));
    const isAccessory = ACCESSORY_KEYWORDS.some(kw => text.includes(kw.toUpperCase()));
    return isGasSpring && !isAccessory;
  }

  // Diversity caps — HARD limits (exceeded = skip entirely)
  const MAX_PER_VENDOR = 3;   // max 3 per brand in 24 products (~12.5%)
  const MAX_PER_L1 = 6;   // max from same top-level category
  const MAX_PER_CATEGORY = 3;   // max from same L3 sub-category
  const MAX_PER_TITLE_FAMILY = 2; // max products sharing same title keyword (e.g. "固定環")

  // ═══════════════════════════════════════════════════════
  // §1b  Responsive Image Builder (LQIP + srcset)
  // ═══════════════════════════════════════════════════════

  function buildResponsiveImg(imgUrl, alt) {
    if (!imgUrl) return '';
    const extMatch = imgUrl.match(/(\.jpg|\.jpeg|\.png|\.webp|\.gif)/i);
    const ext = extMatch ? extMatch[0] : '.jpg';
    // Remove existing size suffix (e.g. _360x) AND extension
    const stem = imgUrl.replace(/(_\d+x)?(\.jpg|\.jpeg|\.png|\.webp|\.gif)(\?.*)?$/i, '');
    const qs = imgUrl.match(/(\?.*)$/)?.[1] || '';
    const widths = [180, 360, 540];
    const srcset = widths
      .map(w => `${stem}_${w}x${ext}${qs} ${w}w`)
      .join(', ');
    const lqip = `${stem}_20x20${ext}${qs}`;
    const fallback = `${stem}_360x${ext}${qs}`;
    const safeAlt = (alt || '').replace(/"/g, '&quot;');
    return `<img class="productitem--image-primary" src="${lqip}" data-src="${fallback}" data-srcset="${srcset}" sizes="(max-width:599px) 46vw,(max-width:1023px) 30vw,22vw" alt="${safeAlt}" width="360" height="360" data-rimg-lqip style="visibility:visible !important; filter:blur(20px);transform:scale(1.1);transition:filter .4s,transform .4s">`;
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function normalizeTags(tags) {
    if (Array.isArray(tags)) return tags.map(String).filter(Boolean);
    if (typeof tags === 'string') return tags.split(',').map(t => t.trim()).filter(Boolean);
    return [];
  }

  function normalizeProductMeta(meta) {
    if (!meta) return null;
    const handle = String(meta.handle || '');
    return {
      el: meta.el || null,
      id: String(meta.id || ''),
      vendor: String(meta.vendor || ''),
      type: String(meta.type || ''),
      tags: normalizeTags(meta.tags),
      created: parseInt(meta.created, 10) || 0,
      hasImage: meta.hasImage === true || meta.hasImage === 'true' || meta.hasImage === 1 || meta.hasImage === '1',
      price: parseInt(meta.price, 10) || 0,
      handle,
      title: String(meta.title || ''),
      url: String(meta.url || (handle ? '/products/' + encodeURIComponent(handle) : '#')),
      image: String(meta.image || ''),
      variantId: meta.variantId ? String(meta.variantId) : '',
    };
  }

  function serializeProductMeta(meta) {
    const normalized = normalizeProductMeta(meta);
    if (!normalized || !normalized.id) return null;
    return {
      id: normalized.id,
      vendor: normalized.vendor,
      type: normalized.type,
      tags: normalized.tags,
      created: normalized.created,
      hasImage: normalized.hasImage,
      price: normalized.price,
      handle: normalized.handle,
      title: normalized.title,
      url: normalized.url,
      image: normalized.image,
      variantId: normalized.variantId,
    };
  }

  function applyMetaDataset(el, meta) {
    el.dataset.poolItem = '';
    el.dataset.productId = meta.id || '';
    el.dataset.vendor = meta.vendor || '';
    el.dataset.type = meta.type || '';
    el.dataset.tags = (meta.tags || []).join(',');
    el.dataset.created = String(meta.created || 0);
    el.dataset.hasImage = meta.hasImage ? '1' : '0';
    el.dataset.ssPrice = String(meta.price || 0);
    el.dataset.handle = meta.handle || '';
    el.dataset.title = meta.title || '';
    el.dataset.url = meta.url || '';
    el.dataset.image = meta.image || '';
    if (meta.variantId) el.dataset.variantId = meta.variantId;
    else delete el.dataset.variantId;
  }

  function buildHomepageCardHtml(product) {
    const title = escapeHtml(product.title || '');
    const vendor = escapeHtml(product.vendor || '');
    const url = escapeHtml(product.url || (product.handle ? '/products/' + encodeURIComponent(product.handle) : '#'));
    const vendorUrl = '/collections/vendors?q=' + encodeURIComponent(product.vendor || '');
    const imageHtml = product.image ? buildResponsiveImg(product.image, product.title || '') : '';
    const variantAttr = product.variantId ? ` data-variant-id="${escapeHtml(product.variantId)}"` : '';

    return `
      <div class="productgrid--item imagestyle--natural show-actions--mobile" data-product-item data-product-quickshop-url="${url}">
        <div class="productitem" data-product-item-content>
          <div class="productitem__container">
            <a class="productitem--image-link" href="${url}">
              <figure class="productitem--image" data-product-item-image>
                <div class="productitem__image-container">
                  ${imageHtml}
                </div>
              </figure>
            </a>
            <div class="productitem--info">
              <h2 class="productitem--title"><a href="${url}">${title}</a></h2>
              <span class="productitem--vendor"><a href="${vendorUrl}">${vendor}</a></span>
            </div>
            <div class="productitem--actions" data-product-actions>
              <div class="productitem--action atc--button">
                <button class="productitem--action-trigger productitem--action-atc button-primary" type="button" data-quick-buy${variantAttr}>
                  <span class="atc-button--text">加入詢價單</span>
                  <span class="atc-button--icon" style="display:none"></span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>`;
  }

  function createHomepageCardElement(product) {
    const wrapper = document.createElement('div');
    wrapper.className = 'zh-grid-item';
    wrapper.setAttribute('data-ss-injected', '');
    applyMetaDataset(wrapper, product);
    wrapper.innerHTML = buildHomepageCardHtml(product);
    product.el = wrapper;
    return wrapper;
  }

  function hydrateHomepageMetaFromCache(id, cached) {
    const meta = normalizeProductMeta(Object.assign({}, cached || {}, { id }));
    if (!meta || !meta.id) return null;
    if (cached && cached.html) {
      const wrapper = document.createElement('div');
      wrapper.className = 'zh-grid-item';
      wrapper.setAttribute('data-ss-injected', '');
      applyMetaDataset(wrapper, meta);
      wrapper.innerHTML = cached.html;
      meta.el = wrapper;
    }
    return meta;
  }

  function ensureHomepageElement(product) {
    if (product.el) return product.el;
    return createHomepageCardElement(product);
  }

  function readFeaturedPool(grid) {
    const section = grid.closest('[id^="shopify-section-"]') || document;
    const script = section.querySelector('[data-featured-pool-json]');
    const visibleItems = Array.from(grid.querySelectorAll('[data-pool-item]'));
    const visibleById = new Map(visibleItems.map(el => [el.dataset.productId || '', el]));

    if (!script) {
      return visibleItems.map(el => parseItemMeta(el)).filter(meta => meta.id);
    }

    try {
      const raw = JSON.parse(script.textContent || '[]');
      return raw.map(normalizeProductMeta).filter(meta => meta && meta.id).map(meta => {
        if (visibleById.has(meta.id)) meta.el = visibleById.get(meta.id);
        return meta;
      });
    } catch (err) {
      console.warn('[SS] Failed to parse featured pool JSON:', err);
      return visibleItems.map(el => parseItemMeta(el)).filter(meta => meta.id);
    }
  }

  function activateAjaxImages(container) {
    if (!container) return;
    container.querySelectorAll('img[data-src]').forEach(img => {
      const io = new IntersectionObserver((entries, obs) => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          const el = entry.target;

          // Setup handlers BEFORE setting src
          el.onload = () => {
            el.style.filter = 'none';
            el.style.transform = 'none';
            el.setAttribute('data-rimg', 'loaded');
            el.removeAttribute('data-rimg-lqip');
          };

          el.onerror = () => {
            // Fallback: try removing _360x (or other size suffix) to get original
            const currentSrc = el.src;
            if (currentSrc.includes('_360x') || currentSrc.includes('_540x')) {
              console.warn('[SS] Image load failed, trying original:', currentSrc);
              const original = currentSrc.replace(/_(360|540|180)x/, '');
              el.src = original;
              // Clear srcset to prevent browser from retrying failed size
              el.removeAttribute('srcset');
            } else {
              // Even original failed - hide to avoid broken icon
              el.style.visibility = 'hidden';
            }
          };

          if (el.dataset.srcset) el.srcset = el.dataset.srcset;
          el.src = el.dataset.src;

          // Handle cached images that load immediately
          if (el.complete) {
            if (el.naturalWidth > 0) {
              el.onload();
            } else {
              // Image already failed before observer ran
              el.onerror();
            }
          }

          obs.unobserve(el);
        });
      }, { rootMargin: '200px' });
      io.observe(img);
    });
  }

  /**
   * Force-load ALL visible images that are stuck in lazy/unloaded state.
   * Handles both rimg-managed (data-rimg="lazy") and AJAX-injected (data-src).
   * Runs with a short delay to ensure DOM has settled after fragment insertion.
   */
  function forceLoadVisibleImages(container) {
    if (!container) return;
    const run = () => {
      // ── Type A: rimg-managed lazy images (Liquid-rendered products) ──
      container.querySelectorAll('img[data-rimg="lazy"]').forEach(img => {
        if (img.closest('.zh-pool-hidden')) return;
        const template = img.getAttribute('data-rimg-template');
        if (!template) return;

        const w = img.offsetWidth || 360;
        const scale = parseFloat(img.getAttribute('data-rimg-scale')) || 1;
        const maxParts = (img.getAttribute('data-rimg-max') || '').split('x').map(Number);
        const maxW = maxParts[0] || 2048;
        const maxH = maxParts[1] || 2048;
        let reqW = Math.min(Math.ceil(w * scale / 32) * 32 || 360, maxW);
        let reqH = Math.min(reqW, maxH);
        const src = template.replace('{size}', reqW + 'x' + reqH);

        img.onload = () => {
          img.setAttribute('data-rimg', 'loaded');
          const canvas = img.nextElementSibling;
          if (canvas && canvas.hasAttribute('data-rimg-canvas')) {
            canvas.style.display = 'none';
          }
          if (img.hasAttribute('data-rimg-lqip')) {
            img.style.filter = 'none';
            img.style.transform = 'none';
            img.removeAttribute('data-rimg-lqip');
          }
        };

        img.src = src;
        if (img.srcset) img.srcset = src; // simple fallback for rimg

        if (img.complete && img.naturalWidth > 0) {
          img.onload();
        }
      });

      // ── Type B: AJAX-injected images with data-src (SmartShuffle) ──
      container.querySelectorAll('img[data-src]').forEach(img => {
        if (img.closest('.zh-pool-hidden')) return;
        if (img.getAttribute('data-rimg') === 'loaded') return;

        // Force load immediately
        img.onload = () => {
          img.style.filter = 'none';
          img.style.transform = 'none';
          img.setAttribute('data-rimg', 'loaded');
          img.removeAttribute('data-rimg-lqip');
        };

        img.onerror = () => {
          // Fallback logic
          const currentSrc = img.src || img.dataset.src;
          if (currentSrc.includes('_360x') || currentSrc.includes('_540x')) {
            console.warn('[SS] ForceLoad failed, trying original:', currentSrc);
            const original = currentSrc.replace(/_(360|540|180)x/, '');
            img.src = original;
            img.removeAttribute('srcset');
          } else {
            img.style.visibility = 'hidden';
          }
        };

        if (img.dataset.srcset) img.srcset = img.dataset.srcset;
        img.src = img.dataset.src;

        if (img.complete) {
          if (img.naturalWidth > 0) img.onload();
          else img.onerror();
        }
      });

      // Nudge rimg's own observer via resize
      window.dispatchEvent(new Event('resize'));
    };


    // Run twice: immediately after rAF, and again after 500ms as safety net
    requestAnimationFrame(run);
    setTimeout(run, 500);
  }

  /**
   * Rescue Squad: Scans for any visible image that is stuck in LQIP state
   * (has data-rimg-lqip attribute) after initial render settles.
   */
  function rescueStuckImages(container) {
    if (!container) return;
    setTimeout(() => {
      const stuck = container.querySelectorAll('img[data-rimg-lqip]:not(.zh-pool-hidden img)');
      if (stuck.length > 0) {
        console.log('[SS] Rescuing', stuck.length, 'stuck images');
        stuck.forEach(img => {
          // Force clear blur
          img.style.filter = 'none';
          img.style.transform = 'none';
          img.removeAttribute('data-rimg-lqip');

          // If not loaded, force reload
          if (!img.complete || img.naturalWidth === 0) {
            const src = img.dataset.src || img.src;
            // Add bust to force reload if needed, but usually just re-setting src works
            img.src = src;
          }
        });
      }
    }, 3000); // 3 seconds after render
  }

  function preloadVisibleImages(displayOrder) {
    const count = Math.min(8, displayOrder.length);
    const run = () => {
      displayOrder.slice(0, count).forEach(p => {
        if (!p.el) return;
        const img = p.el.querySelector('img[data-src]');
        if (!img || !img.dataset.src) return;
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        link.href = img.dataset.src;
        if (img.dataset.srcset) link.setAttribute('imagesrcset', img.dataset.srcset);
        document.head.appendChild(link);
      });
    };
    if ('requestIdleCallback' in window) requestIdleCallback(run, { timeout: 2000 });
    else setTimeout(run, 100);
  }

  // ═══════════════════════════════════════════════════════
  // §2  Utility: Seeded Random (kept for daily/weekly compat)
  // ═══════════════════════════════════════════════════════

  function mulberry32(seed) {
    return function () {
      let t = (seed += 0x6d2b79f5);
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  function dateSeed(mode) {
    const now = new Date();
    if (mode === 'weekly') {
      const start = new Date(now.getFullYear(), 0, 1);
      const week = Math.ceil(((now - start) / 86400000 + start.getDay() + 1) / 7);
      return now.getFullYear() * 100 + week;
    }
    return now.getFullYear() * 10000 + (now.getMonth() + 1) * 100 + now.getDate();
  }

  function seededShuffle(arr, seed) {
    const rng = mulberry32(seed);
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  }

  function shuffle(arr) {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  }

  // ═══════════════════════════════════════════════════════
  // §3  localStorage Helpers
  // ═══════════════════════════════════════════════════════

  function lsGet(key, fallback) {
    try { return JSON.parse(localStorage.getItem(key)) || fallback; }
    catch { return fallback; }
  }

  function lsSet(key, value) {
    try { localStorage.setItem(key, JSON.stringify(value)); }
    catch { /* quota exceeded or private mode */ }
  }

  // ═══════════════════════════════════════════════════════
  // §4  User Signals
  // ═══════════════════════════════════════════════════════

  function getSignals() {
    return lsGet(SIGNALS_KEY, {
      viewed_vendors: [],
      viewed_categories: [],
      viewed_products: [],
      visit_count: 0,
    });
  }

  function saveSignals(signals) { lsSet(SIGNALS_KEY, signals); }

  function trackProductView() {
    const meta = document.querySelector('meta[property="og:type"][content="product"]');
    if (!meta) return;
    const idEl = document.querySelector('[data-product-id]');
    const productId = idEl ? idEl.dataset.productId : null;
    if (!productId) return;

    const signals = getSignals();
    signals.viewed_products = [productId, ...signals.viewed_products.filter(id => id !== productId)].slice(0, MAX_HISTORY);

    const vendorEl = document.querySelector('.productitem--vendor, [data-product-vendor]');
    if (vendorEl) {
      const vendor = vendorEl.dataset.productVendor || vendorEl.textContent.trim();
      if (vendor) signals.viewed_vendors = [vendor, ...signals.viewed_vendors.filter(v => v !== vendor)].slice(0, 10);
    }

    const collHandle = document.querySelector('[data-collection-handle]');
    if (collHandle) {
      const handle = collHandle.dataset.collectionHandle;
      if (handle) signals.viewed_categories = [handle, ...signals.viewed_categories.filter(h => h !== handle)].slice(0, 10);
    }

    saveSignals(signals);
    lsSet(HISTORY_KEY, signals.viewed_products);
  }

  function trackPageView() {
    const signals = getSignals();
    signals.visit_count = (signals.visit_count || 0) + 1;
    const grid = document.querySelector('[data-smartshuffle-collection]');
    if (grid) {
      const handle = grid.dataset.smartshuffleCollection;
      if (handle) signals.viewed_categories = [handle, ...signals.viewed_categories.filter(h => h !== handle)].slice(0, 10);
    }
    saveSignals(signals);
  }

  // ═══════════════════════════════════════════════════════
  // §5  Anti-Fatigue Memory
  // ═══════════════════════════════════════════════════════

  function getMemory() { return lsGet(MEMORY_KEY, { visits: [] }); }

  function recordShown(productIds) {
    const memory = getMemory();
    memory.visits.unshift(productIds);
    memory.visits = memory.visits.slice(0, MAX_MEMORY_VISITS);
    lsSet(MEMORY_KEY, memory);
  }

  function buildFatigueMap() {
    const memory = getMemory();
    const map = {};
    memory.visits.forEach((ids, visitIndex) => {
      const mul = W.fatigueMul[visitIndex];
      if (mul === undefined) return;
      (ids || []).forEach(id => {
        if (!(id in map) || mul < map[id]) map[id] = mul;
      });
    });
    return map;
  }

  // ═══════════════════════════════════════════════════════
  // §5b  Multi-Level Category Tracker (L1/L2/L3)
  // ═══════════════════════════════════════════════════════

  // M-code structure: M 01 01 00 0000
  //                     L1 L2 L3  leaf
  function extractCatLevels(product) {
    const tags = product.tags || [];
    for (const t of tags) {
      const m = t.match(/^(?:col-)?[Mm](\d{10})$/);
      if (m) {
        const code = m[1]; // "0101000000"
        return {
          l1: 'L1_' + code.substring(0, 2),           // "L1_01"
          l2: 'L2_' + code.substring(0, 4),           // "L2_0101"
          l3: 'L3_' + code.substring(0, 6),           // "L3_010100"
        };
      }
    }
    // Fallback: vendor-based grouping
    const v = product.vendor || '_unknown_';
    return { l1: 'V_' + v, l2: 'V_' + v, l3: 'V_' + v };
  }

  // Backward compat wrapper
  function extractL3(product) {
    return extractCatLevels(product).l3;
  }

  function getCatExposure() {
    return lsGet(CAT_HISTORY_KEY, { visits: [] });
  }

  function recordCatExposure(products) {
    const record = { l1: {}, l2: {}, l3: {} };
    products.forEach(p => {
      const cats = extractCatLevels(p);
      record.l1[cats.l1] = (record.l1[cats.l1] || 0) + 1;
      record.l2[cats.l2] = (record.l2[cats.l2] || 0) + 1;
      record.l3[cats.l3] = (record.l3[cats.l3] || 0) + 1;
    });
    const history = getCatExposure();
    history.visits.unshift(record);
    history.visits = history.visits.slice(0, CAT_WINDOW);
    lsSet(CAT_HISTORY_KEY, history);
  }

  function buildCatExposureMap() {
    // Returns { l1: {code: totalCount}, l2: {...}, l3: {...} }
    const history = getCatExposure();
    const map = { l1: {}, l2: {}, l3: {} };
    history.visits.forEach(record => {
      if (!record || !record.l1) {
        // Legacy format compatibility: treat as l3
        for (const [cat, count] of Object.entries(record || {})) {
          map.l3[cat] = (map.l3[cat] || 0) + count;
        }
        return;
      }
      for (const level of ['l1', 'l2', 'l3']) {
        for (const [cat, count] of Object.entries(record[level] || {})) {
          map[level][cat] = (map[level][cat] || 0) + count;
        }
      }
    });
    return map;
  }

  // ═══════════════════════════════════════════════════════
  // §6  Scoring Engine
  // ═══════════════════════════════════════════════════════

  function parseItemMeta(el) {
    const titleLink = el.querySelector('.productitem--title a');
    const imageEl = el.querySelector('img');
    return normalizeProductMeta({
      el,
      id: el.dataset.productId || '',
      vendor: el.dataset.vendor || '',
      type: el.dataset.type || '',
      tags: (el.dataset.tags || '').split(',').filter(Boolean),
      created: parseInt(el.dataset.created, 10) || 0,
      hasImage: el.dataset.hasImage === '1',
      price: parseInt(el.dataset.ssPrice, 10) || 0,
      handle: el.dataset.handle || '',
      title: el.dataset.title || (titleLink ? titleLink.textContent.trim() : ''),
      url: el.dataset.url || (titleLink ? titleLink.getAttribute('href') || '' : ''),
      image: el.dataset.image || (imageEl ? imageEl.getAttribute('data-src') || imageEl.getAttribute('src') || '' : ''),
      variantId: el.dataset.variantId || (el.querySelector('[data-variant-id]')?.getAttribute('data-variant-id') || ''),
    });
  }

  function scoreProduct(product, signals, fatigueMap, catExposureMap) {
    let w = W.base;

    // ── Freshness ──
    const now = Math.floor(Date.now() / 1000);
    const age = now - product.created;
    if (age < 30 * 86400) w += W.freshness30d;
    else if (age < 90 * 86400) w += W.freshness90d;

    // ── Affinity: vendor ──
    if (signals.viewed_vendors.includes(product.vendor)) {
      w += W.affinityVendor;
    }

    // ── Affinity: category ──
    const viewedCats = new Set(signals.viewed_categories);
    const hasOverlap = product.tags.some(t => viewedCats.has(t));
    if (hasOverlap || viewedCats.has(product.type)) {
      w += W.affinityCategory;
    }

    // ── Image bonus ──
    if (product.hasImage) w += W.imageBonus;

    // ── Exploration: vendor NOT in history ──
    if (!signals.viewed_vendors.includes(product.vendor)) {
      w += W.exploration;
    }

    // ── Partner vendor boost ──
    if (isPascal(product.vendor)) {
      // PASCAL is the premier partner — massive weight
      w += 0.65;
      // Extra boost for nitrogen gas spring main products
      if (isPascalGasSpring(product)) {
        w += 0.40;  // total PASCAL gas spring: base + 0.65 + 0.40 = 2.05
      }
    } else if (isPartner(product.vendor)) {
      w += 0.35;  // other friendly suppliers
    }

    // ── Multi-level category distance scoring ──
    // Strongly prefer products from distant L1/L2 branches
    if (catExposureMap && catExposureMap.l1) {
      const cats = extractCatLevels(product);
      const l1Exp = catExposureMap.l1[cats.l1] || 0;
      const l2Exp = catExposureMap.l2[cats.l2] || 0;
      const l3Exp = catExposureMap.l3[cats.l3] || 0;

      // ── Additive bonuses for unexplored branches ──
      if (l1Exp === 0) w += W.catNewL1;          // whole L1 branch is fresh
      else if (l2Exp === 0) w += W.catNewL2;     // same L1 but different L2
      else if (l3Exp === 0) w += W.catNewL3;     // same L2 but different L3

      // ── Multiplicative penalties for over-exposed branches ──
      // L1 shown > 20 times across window → heavy penalty (force jump to other L1)
      if (l1Exp > 20) w *= W.catOverexposedL1;
      // L2 shown > 10 times → medium penalty
      else if (l2Exp > 10) w *= W.catOverexposedL2;
    }

    // ── Anti-fatigue (multiplicative) ──
    if (product.id in fatigueMap) {
      w *= fatigueMap[product.id];
    }

    return Math.max(w, 0.005);
  }

  // ═══════════════════════════════════════════════════════
  // §7  Diversity-Constrained Weighted Sampler
  // ═══════════════════════════════════════════════════════

  /**
   * Extract a "title family" — combine vendor + first CJK keyword.
   * "3A-2H 夾具" by CONSUSS → "CONSUSS_夾具"
   * "固定環 A型" by IWATA → "IWATA_固定環"
   * Products from different brands but same keyword are NOT de-duped (that's fine).
   */
  function titleFamily(product) {
    const raw = (product.title || '').trim();
    // Strip leading model numbers/codes
    const stripped = raw.replace(/^[\w\-.\s/()]+/, '').trim();
    // Take first CJK keyword (2-4 chars)
    const cjk = stripped.match(/[\u4e00-\u9fff\u3040-\u309f\u30a0-\u30ff]{2,4}/);
    const keyword = cjk ? cjk[0] : raw.substring(0, 8);
    // Scope by vendor so only SAME brand + SAME keyword gets de-duped
    return (product.vendor || '') + '_' + keyword;
  }

  /**
   * Diversity-constrained weighted sampler with HARD blocks.
   * Accepts optional pre-existing counts (from keepers in gradual drift).
   */
  function weightedSample(pool, targetCount, maxPerVendor, maxPerCategory, existingCounts) {
    const selected = [];
    const remaining = new Set(pool.map((_, i) => i));
    const vendorCounts = Object.assign({}, existingCounts?.vendor || {});
    const l1Counts = Object.assign({}, existingCounts?.l1 || {});
    const l3Counts = Object.assign({}, existingCounts?.l3 || {});
    const familyCounts = Object.assign({}, existingCounts?.family || {});

    while (selected.length < targetCount && remaining.size > 0) {
      let totalWeight = 0;
      const candidates = [];

      for (const idx of remaining) {
        const p = pool[idx];
        let ew = p.score;
        const cats = extractCatLevels(p);
        const fam = titleFamily(p);

        // HARD BLOCK: skip entirely if any diversity cap is reached
        if ((vendorCounts[p.vendor] || 0) >= maxPerVendor) continue;
        if ((l1Counts[cats.l1] || 0) >= MAX_PER_L1) continue;
        if ((l3Counts[cats.l3] || 0) >= maxPerCategory) continue;
        if ((familyCounts[fam] || 0) >= MAX_PER_TITLE_FAMILY) continue;

        totalWeight += ew;
        candidates.push({ idx, ew });
      }

      if (totalWeight <= 0 || candidates.length === 0) break;

      let r = Math.random() * totalWeight;
      let pick = candidates[candidates.length - 1].idx;
      for (const c of candidates) {
        r -= c.ew;
        if (r <= 0) { pick = c.idx; break; }
      }

      const p = pool[pick];
      selected.push(p);
      remaining.delete(pick);
      vendorCounts[p.vendor] = (vendorCounts[p.vendor] || 0) + 1;
      const cats = extractCatLevels(p);
      l1Counts[cats.l1] = (l1Counts[cats.l1] || 0) + 1;
      l3Counts[cats.l3] = (l3Counts[cats.l3] || 0) + 1;
      const fam = titleFamily(p);
      familyCounts[fam] = (familyCounts[fam] || 0) + 1;
    }

    return selected;
  }

  // ═══════════════════════════════════════════════════════
  // §7b  Partner-Split Sampler (50% partner guaranteed)
  // ═══════════════════════════════════════════════════════

  /**
   * Splits pool into partner / non-partner, samples PARTNER_RATIO from partner
   * pool and the rest from non-partner, then merges. Falls back gracefully when
   * either sub-pool is too small.
   */
  function partnerSplitSample(pool, targetCount) {
    const partnerPool = pool.filter(p => isPartner(p.vendor));
    const otherPool = pool.filter(p => !isPartner(p.vendor));

    const partnerSlots = Math.min(
      Math.ceil(targetCount * PARTNER_RATIO),
      partnerPool.length
    );
    const otherSlots = Math.min(targetCount - partnerSlots, otherPool.length);

    // Phase 1: partner picks
    const partnerPicks = weightedSample(partnerPool, partnerSlots, MAX_PER_VENDOR, MAX_PER_CATEGORY);

    // Phase 2: other picks — pass partner counts to enforce GLOBAL caps
    const afterPartnerCounts = buildDiversityCounts(partnerPicks);
    const otherPicks = weightedSample(otherPool, otherSlots, MAX_PER_VENDOR, MAX_PER_CATEGORY, afterPartnerCounts);

    let combined = [...partnerPicks, ...otherPicks];

    // Phase 3: backfill if needed — pass combined counts
    if (combined.length < targetCount) {
      const usedIds = new Set(combined.map(p => p.id));
      const backfillPool = pool.filter(p => !usedIds.has(p.id));
      const afterCombinedCounts = buildDiversityCounts(combined);
      const backfill = weightedSample(backfillPool, targetCount - combined.length, MAX_PER_VENDOR, MAX_PER_CATEGORY, afterCombinedCounts);
      combined = [...combined, ...backfill];
    }

    return combined;
  }

  /**
   * Build diversity count maps from a list of products.
   * Used to seed the sampler with existing selections (keepers).
   */
  function buildDiversityCounts(products) {
    const vendor = {}, l1 = {}, l3 = {}, family = {};
    products.forEach(p => {
      vendor[p.vendor] = (vendor[p.vendor] || 0) + 1;
      const cats = extractCatLevels(p);
      l1[cats.l1] = (l1[cats.l1] || 0) + 1;
      l3[cats.l3] = (l3[cats.l3] || 0) + 1;
      const fam = titleFamily(p);
      family[fam] = (family[fam] || 0) + 1;
    });
    return { vendor, l1, l3, family };
  }

  /**
   * For gradual drift: pick fresh items while maintaining partner ratio.
   * Passes keeper diversity counts so fresh picks respect GLOBAL caps.
   */
  function partnerAwareFreshPicks(freshPool, needed, keepers, totalTarget) {
    const partnerKeepers = keepers.filter(p => isPartner(p.vendor)).length;
    const desiredPartnerTotal = Math.ceil(totalTarget * PARTNER_RATIO);
    const partnerFreshNeeded = Math.max(0, desiredPartnerTotal - partnerKeepers);

    // Build existing counts from keepers so sampler respects global caps
    const keeperCounts = buildDiversityCounts(keepers);

    const freshPartner = freshPool.filter(p => isPartner(p.vendor));
    const freshOther = freshPool.filter(p => !isPartner(p.vendor));

    const partnerCount = Math.min(partnerFreshNeeded, freshPartner.length, needed);
    const otherCount = Math.min(needed - partnerCount, freshOther.length);

    const partnerPicks = weightedSample(freshPartner, partnerCount, MAX_PER_VENDOR, MAX_PER_CATEGORY, keeperCounts);

    // Merge keeper + partner pick counts for other-pool sampling
    const afterPartnerCounts = buildDiversityCounts([...keepers, ...partnerPicks]);
    const otherPicks = weightedSample(freshOther, otherCount, MAX_PER_VENDOR, MAX_PER_CATEGORY, afterPartnerCounts);

    let fresh = [...partnerPicks, ...otherPicks];

    // Backfill if either sub-pool was insufficient
    if (fresh.length < needed) {
      const usedIds = new Set(fresh.map(p => p.id));
      const backfillPool = freshPool.filter(p => !usedIds.has(p.id));
      const afterAllCounts = buildDiversityCounts([...keepers, ...fresh]);
      const backfill = weightedSample(backfillPool, needed - fresh.length, MAX_PER_VENDOR, MAX_PER_CATEGORY, afterAllCounts);
      fresh = [...fresh, ...backfill];
    }

    return fresh;
  }

  /**
   * Post-selection diversity enforcement: if any vendor exceeds MAX_PER_VENDOR
   * in the final combined display, drop excess and replace from pool.
   */
  function enforceDiversity(display, pool, showCount) {
    const vendorCounts = {};
    const l3Counts = {};
    const familyCounts = {};
    const keep = [];
    const dropped = new Set();

    // Pass 1: keep products respecting caps, drop excess
    for (const p of display) {
      const vc = vendorCounts[p.vendor] || 0;
      const cats = extractCatLevels(p);
      const lc = l3Counts[cats.l3] || 0;
      const fam = titleFamily(p);
      const fc = familyCounts[fam] || 0;

      if (vc >= MAX_PER_VENDOR || lc >= MAX_PER_CATEGORY || fc >= MAX_PER_TITLE_FAMILY) {
        dropped.add(p.id);
      } else {
        keep.push(p);
        vendorCounts[p.vendor] = vc + 1;
        l3Counts[cats.l3] = lc + 1;
        familyCounts[fam] = fc + 1;
      }
    }

    // Pass 2: fill dropped slots from pool
    if (keep.length < showCount) {
      const usedIds = new Set(keep.map(p => p.id));
      const replacementPool = pool.filter(p => !usedIds.has(p.id));
      const existingCounts = buildDiversityCounts(keep);
      const replacements = weightedSample(replacementPool, showCount - keep.length, MAX_PER_VENDOR, MAX_PER_CATEGORY, existingCounts);
      keep.push(...replacements);
    }

    return keep;
  }

  // ═══════════════════════════════════════════════════════
  // §8  Gradual Drift: Homepage "精選產品"
  // ═══════════════════════════════════════════════════════

  function applyToGrid(items, grid, displayOrder) {
    const selectedIds = new Set(displayOrder.map(p => p.id));
    const currentItems = Array.from(grid.querySelectorAll('[data-pool-item]'));

    currentItems.forEach(item => {
      if (selectedIds.has(item.dataset.productId)) {
        item.classList.remove('zh-pool-hidden');
        return;
      }

      if (item.hasAttribute('data-ss-injected')) {
        item.remove();
        return;
      }

      item.classList.add('zh-pool-hidden');
    });

    const frag = document.createDocumentFragment();
    displayOrder.forEach(p => {
      const el = ensureHomepageElement(p);
      el.classList.remove('zh-pool-hidden');
      frag.appendChild(el);
    });
    grid.replaceChildren(frag);
    activateAjaxImages(grid);
    forceLoadVisibleImages(grid);
    preloadVisibleImages(displayOrder);
    rescueStuckImages(grid);
    document.dispatchEvent(new CustomEvent('zoro:rotation', {
      detail: { target: 'home-featured' }
    }));
  }

  async function initHomepageShuffle() {
    const grid = document.querySelector('[data-featured-pool]');
    if (!grid) return;

    const mode = grid.dataset.rotationMode || 'daily';
    const showCount = parseInt(grid.dataset.showCount, 10) || 24;
    const pool = readFeaturedPool(grid);
    const poolById = new Map(pool.map(p => [p.id, p]));

    // ── Legacy modes ──
    if (mode === 'daily' || mode === 'weekly') {
      const seed = dateSeed(mode);
      const displayOrder = seededShuffle(pool, seed).slice(0, showCount);
      applyToGrid([], grid, displayOrder);
      return;
    }
    if (mode === 'static') return;
    if (pool.length <= showCount) return;

    // ══════════════════════════════════════════════════════════
    // CACHE-FIRST ARCHITECTURE (World-Class Instant F5)
    //
    // 1. INSTANT: Read pre-computed display from localStorage → apply NOW
    // 2. BACKGROUND: AJAX enrich pool → compute NEXT visit's display → cache
    // ══════════════════════════════════════════════════════════

    const NEXT_DISPLAY_KEY = DISPLAY_KEY + '_home_next';
    const NEXT_META_KEY = DISPLAY_KEY + '_home_next_meta';
    const HOME_DISPLAY_KEY = DISPLAY_KEY + '_home';

    // ── Phase 0: INSTANT cache replay (0ms latency) ──
    const nextDisplayIds = lsGet(NEXT_DISPLAY_KEY, []);
    const nextMetaCache = lsGet(NEXT_META_KEY, {});
    Object.keys(nextMetaCache).forEach(id => {
      if (poolById.has(id)) return;
      const meta = hydrateHomepageMetaFromCache(id, nextMetaCache[id]);
      if (!meta) return;
      pool.push(meta);
      poolById.set(meta.id, meta);
    });

    if (nextDisplayIds.length > 0) {
      // Now resolve cached display from the enriched pool
      const cachedInPool = nextDisplayIds
        .map(id => poolById.get(id))
        .filter(Boolean);

      if (cachedInPool.length >= Math.ceil(showCount * 0.6)) {
        let instantDisplay = cachedInPool.slice(0, showCount);

        if (instantDisplay.length < showCount) {
          const usedIds = new Set(instantDisplay.map(p => p.id));
          const fillers = Array.from(poolById.values())
            .filter(p => !usedIds.has(p.id));
          shuffle(fillers);
          instantDisplay = [...instantDisplay, ...fillers.slice(0, showCount - instantDisplay.length)];
        }

        // Global diversity enforcement even on cache replay
        instantDisplay = enforceDiversity(instantDisplay, Array.from(poolById.values()), showCount);

        applyToGrid([], grid, instantDisplay);
        lsSet(HOME_DISPLAY_KEY, instantDisplay.map(p => p.id));
        recordShown(instantDisplay.map(p => p.id));
        recordCatExposure(instantDisplay);

        backgroundPrecompute(grid, [], poolById, showCount, instantDisplay);
        return;
      }
    }

    // ── First visit or cache miss: synchronous Liquid-only shuffle ──
    // Show something reasonable IMMEDIATELY from the 120-product Liquid pool
    const signals = getSignals();
    const fatigueMap = buildFatigueMap();
    const catExposureMap = buildCatExposureMap();

    pool.forEach(p => { p.score = scoreProduct(p, signals, fatigueMap, catExposureMap); });

    // Quick partner-split from Liquid pool only (synchronous, instant)
    const quickDisplay = partnerSplitSample(pool, showCount);
    const diverseDisplay = enforceDiversity(quickDisplay, pool, showCount);
    const displayOrder = shuffle(diverseDisplay);
    applyToGrid([], grid, displayOrder);
    lsSet(HOME_DISPLAY_KEY, displayOrder.map(p => p.id));
    recordShown(displayOrder.map(p => p.id));
    recordCatExposure(displayOrder);

    // Background: AJAX enrich → pre-compute NEXT visit's display
    backgroundPrecompute(grid, [], poolById, showCount, displayOrder);
  }

  /**
   * Background pre-computation: AJAX enrich the pool, compute next visit's
   * display order, and cache it. Does NOT change the current visible grid.
   */
  async function backgroundPrecompute(grid, items, liquidPoolById, showCount, currentDisplay) {
    const signals = getSignals();
    const fatigueMap = buildFatigueMap();
    const catExposureMap = buildCatExposureMap();

    let pool = Array.from(liquidPoolById.values());
    pool.forEach(p => { p.score = scoreProduct(p, signals, fatigueMap, catExposureMap); });

    const collHandle = grid.dataset.collectionHandle || 'all';
    const totalPages = parseInt(grid.dataset.totalPages, 10) || 1;
    const existingIds = new Set(pool.map(p => p.id));

    if (totalPages > 1) {
      // Phase 1: Random pages for broad diversity (4 pages for larger pool)
      const randomPages = pickRandomPages(totalPages, 1, Math.min(4, totalPages - 1));
      const randomFetched = await fetchAndInjectForHomepage(collHandle, randomPages, existingIds);
      randomFetched.forEach(meta => {
        meta.score = scoreProduct(meta, signals, fatigueMap, catExposureMap);
        pool.push(meta);
      });

      // Phase 2: Targeted search for missing partner vendors
      const vendorsInPool = new Set(pool.filter(p => isPartner(p.vendor)).map(p => p.vendor.toUpperCase()));
      const missingVendors = PARTNER_VENDORS.filter(v => {
        return !Array.from(vendorsInPool).some(pv => pv.indexOf(v.toUpperCase()) >= 0);
      });

      if (missingVendors.length > 0) {
        const vendorFetches = missingVendors.map(async (vendor) => {
          try {
            // Fetch search results HTML
            const res = await fetch(`/search?type=product&q=${encodeURIComponent(vendor)}`);
            if (!res.ok) return [];
            const html = await res.text();
            const doc = new DOMParser().parseFromString(html, 'text/html');

            // Extract product handles via getAttribute (avoids DOMParser base URL issue)
            const handleSet = new Set();
            doc.querySelectorAll('a').forEach(a => {
              const href = a.getAttribute('href') || '';
              const m = href.match(/\/products\/([^?#]+)/);
              if (m) handleSet.add(decodeURIComponent(m[1]));
            });

            const handles = Array.from(handleSet).slice(0, 6);
            if (handles.length === 0) return [];

            const results = [];
            const jsonFetches = handles.map(async (handle) => {
              try {
                const pRes = await fetch(`/products/${encodeURIComponent(handle)}.json`);
                if (!pRes.ok) return;
                const pData = await pRes.json();
                const p = pData.product;
                if (!p || existingIds.has(String(p.id))) return;
                // Verify this product is from the target vendor
                if (!(p.vendor || '').toUpperCase().includes(vendor.toUpperCase())) return;

                const wrapper = document.createElement('div');
                wrapper.className = 'zh-grid-item zh-pool-hidden';
                wrapper.dataset.poolItem = '';
                wrapper.dataset.productId = String(p.id);
                wrapper.dataset.vendor = p.vendor || '';
                wrapper.dataset.type = p.product_type || '';
                wrapper.dataset.tags = (p.tags || '').toString();
                wrapper.dataset.created = String(Math.floor(new Date(p.created_at).getTime() / 1000));
                wrapper.dataset.hasImage = p.images && p.images.length > 0 ? '1' : '0';
                wrapper.dataset.ssPrice = String(Math.round((parseFloat(p.variants?.[0]?.price) || 0) * 100));
                wrapper.dataset.handle = handle;
                wrapper.setAttribute('data-ss-injected', '');

                // Build theme-compatible product card with action buttons
                const pUrl = '/products/' + encodeURIComponent(handle);
                const img = p.images?.[0]?.src || '';
                if (!img) return; // Skip products with no images
                const variantId = p.variants?.[0]?.id || '';
                const imgHtml = buildResponsiveImg(img, p.title);
                wrapper.innerHTML = `
                  <div class="productgrid--item imagestyle--natural show-actions--mobile" data-product-item data-product-quickshop-url="${pUrl}">
                    <div class="productitem" data-product-item-content>
                      <div class="productitem__container">
                        <a class="productitem--image-link" href="${pUrl}">
                          <figure class="productitem--image" data-product-item-image>
                            <div class="productitem__image-container">
                              ${imgHtml}
                            </div>
                          </figure>
                        </a>
                        <div class="productitem--info">
                          <h2 class="productitem--title"><a href="${pUrl}">${p.title}</a></h2>
                          <span class="productitem--vendor"><a href="/collections/vendors?q=${encodeURIComponent(p.vendor)}">${p.vendor}</a></span>
                        </div>
                        <div class="productitem--actions" data-product-actions>
                          <!-- 直接詢價 removed -->
                          <div class="productitem--action atc--button">
                            <button class="productitem--action-trigger productitem--action-atc button-primary" type="button" data-quick-buy data-variant-id="${variantId}">
                              <span class="atc-button--text">加入詢價單</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>`;

                existingIds.add(String(p.id));
                results.push(parseItemMeta(wrapper));
              } catch { /* silent */ }
            });

            await Promise.all(jsonFetches);
            return results;
          } catch { return []; }
        });

        const vendorResults = await Promise.all(vendorFetches);
        vendorResults.flat().forEach(meta => {
          meta.score = scoreProduct(meta, signals, fatigueMap, catExposureMap);
          pool.push(meta);
        });
      }
    }

    // ── Compute NEXT visit's display (gradual drift from current) ──
    const poolById = new Map(pool.map(p => [p.id, p]));
    const currentIds = currentDisplay.map(p => p.id);

    const swapCount = Math.max(1, Math.ceil(showCount * SWAP_RATE));
    const keepCount = showCount - swapCount;

    // Keepers: randomly retain ~60% of current display, pre-filtered for diversity
    const currentInPool = currentIds.map(id => poolById.get(id)).filter(Boolean);
    const rawKeepers = shuffle(currentInPool).slice(0, Math.min(keepCount, currentInPool.length));
    // Enforce diversity on keepers themselves (legacy sessions may have violations)
    const keepers = enforceDiversity(rawKeepers, rawKeepers, rawKeepers.length);
    const keeperIds = new Set(keepers.map(p => p.id));

    // Fresh candidates from the full enriched pool
    const freshPool = pool.filter(p => !keeperIds.has(p.id));
    const needed = showCount - keepers.length;
    const freshPicks = partnerAwareFreshPicks(freshPool, needed, keepers, showCount);

    // Enforce global diversity on the combined result
    const rawCombined = [...keepers, ...freshPicks];
    const diverseDisplay = enforceDiversity(rawCombined, pool, showCount);
    const nextDisplay = shuffle(diverseDisplay);

    // Save pre-computed display for NEXT visit (not applied now!)
    const NEXT_KEY = DISPLAY_KEY + '_home_next';
    const NEXT_META_KEY = DISPLAY_KEY + '_home_next_meta';
    lsSet(NEXT_KEY, nextDisplay.map(p => p.id));
    const metaCache = {};
    nextDisplay.forEach(p => {
      const serialized = serializeProductMeta(p);
      if (!serialized) return;
      metaCache[p.id] = serialized;
    });
    lsSet(NEXT_META_KEY, metaCache);
  }

  // ═══════════════════════════════════════════════════════
  // §9  Gradual Drift: Collection Page
  // ═══════════════════════════════════════════════════════

  async function initCollectionShuffle() {
    const grid = document.querySelector('[data-smartshuffle-collection]');
    if (!grid) return;

    const params = new URLSearchParams(window.location.search);
    if (params.has('q') || params.has('sort_by') || params.has('constraint')) return;
    const currentPage = parseInt(grid.dataset.smartshuffleCurrentPage, 10) || 1;
    if (currentPage > 1) return;

    const handle = grid.dataset.smartshuffleCollection;
    const totalPages = parseInt(grid.dataset.smartshuffleTotalPages, 10) || 1;
    const perPage = parseInt(grid.dataset.smartshufflePerPage, 10) || 48;

    const onPageItems = Array.from(grid.querySelectorAll('[data-ss-item]'));
    if (onPageItems.length === 0) return;

    const signals = getSignals();
    const fatigueMap = buildFatigueMap();
    const catExposureMap = buildCatExposureMap();

    let pool = onPageItems.map(el => {
      const meta = parseItemMeta(el);
      meta.score = scoreProduct(meta, signals, fatigueMap, catExposureMap);
      meta.source = 'onpage';
      return meta;
    });

    // AJAX pool expansion
    if (totalPages > 1) {
      const pagesToFetch = pickRandomPages(totalPages, currentPage, 2);
      const fetched = await fetchRemoteProducts(handle, pagesToFetch);
      fetched.forEach(meta => {
        if (pool.some(p => p.id === meta.id)) return;
        meta.score = scoreProduct(meta, signals, fatigueMap, catExposureMap);
        meta.source = 'ajax';
        pool.push(meta);
      });
    }

    const poolById = new Map(pool.map(p => [p.id, p]));
    const displayKey = DISPLAY_KEY + '_col_' + handle;
    const prevDisplayIds = lsGet(displayKey, []);

    // First visit or invalid state – use partner-split sampler
    if (prevDisplayIds.length === 0) {
      const targetCount = Math.min(perPage, pool.length);
      const selected = partnerSplitSample(pool, targetCount);
      const displayOrder = shuffle(selected);
      applyCollectionGrid(grid, onPageItems, displayOrder);
      lsSet(displayKey, displayOrder.map(p => p.id));
      recordShown(displayOrder.map(p => p.id));
      recordCatExposure(displayOrder);
      return;
    }

    // Gradual rotation – partner-aware fresh picks
    const targetCount = Math.min(perPage, pool.length);
    const swapCount = Math.max(1, Math.ceil(targetCount * SWAP_RATE));
    const keepCount = targetCount - swapCount;

    const prevInPool = prevDisplayIds.map(id => poolById.get(id)).filter(Boolean);
    const keepers = shuffle(prevInPool).slice(0, Math.min(keepCount, prevInPool.length));
    const keeperIds = new Set(keepers.map(p => p.id));
    const freshPool = pool.filter(p => !keeperIds.has(p.id));
    const needed = targetCount - keepers.length;
    const freshPicks = partnerAwareFreshPicks(freshPool, needed, keepers, targetCount);

    const combined = [...keepers, ...freshPicks];
    const displayOrder = shuffle(combined);

    applyCollectionGrid(grid, onPageItems, displayOrder);
    lsSet(displayKey, displayOrder.map(p => p.id));
    recordShown(displayOrder.map(p => p.id));
    recordCatExposure(displayOrder);
  }

  function applyCollectionGrid(grid, onPageItems, displayOrder) {
    const selectedIds = new Set(displayOrder.map(p => p.id));
    const fragment = document.createDocumentFragment();
    displayOrder.forEach(p => fragment.appendChild(p.el));
    onPageItems.forEach(item => {
      if (!selectedIds.has(item.dataset.productId)) item.style.display = 'none';
    });
    grid.prepend(fragment);
  }

  function pickRandomPages(totalPages, excludePage, count) {
    const available = [];
    for (let i = 1; i <= totalPages; i++) {
      if (i !== excludePage) available.push(i);
    }
    return shuffle(available).slice(0, count);
  }

  async function fetchRemoteProducts(handle, pageNumbers) {
    const results = [];
    const fetches = pageNumbers.map(async (page) => {
      try {
        const res = await fetch(`/collections/${handle}?page=${page}`);
        if (!res.ok) return;
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        doc.querySelectorAll('[data-ss-item]').forEach(el => {
          results.push(parseItemMeta(el));
        });
      } catch { /* silent */ }
    });
    await Promise.all(fetches);
    return results;
  }

  /**
   * Fetch remote products AND inject proper homepage grid-item wrappers
   * into the grid so they can be displayed. Returns enriched meta objects
   * with .el pointing to the new DOM node.
   */
  async function fetchAndInjectForHomepage(handle, pageNumbers, existingIds) {
    const results = [];
    const fetches = pageNumbers.map(async (page) => {
      try {
        const res = await fetch(`/collections/${handle}?page=${page}`);
        if (!res.ok) return;
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        // Collection pages use [data-ss-item]; extract the inner product card
        doc.querySelectorAll('[data-ss-item]').forEach(src => {
          const pid = src.dataset.productId || '';
          if (!pid || existingIds.has(pid)) return;

          // Create a homepage-compatible wrapper
          const wrapper = document.createElement('div');
          wrapper.className = 'zh-grid-item zh-pool-hidden';
          wrapper.dataset.poolItem = '';
          wrapper.dataset.productId = pid;
          wrapper.dataset.vendor = src.dataset.vendor || '';
          wrapper.dataset.type = src.dataset.type || '';
          wrapper.dataset.tags = src.dataset.tags || '';
          wrapper.dataset.created = src.dataset.created || '';
          wrapper.dataset.hasImage = src.dataset.hasImage || '0';
          wrapper.dataset.ssPrice = src.dataset.ssPrice || '0';
          wrapper.dataset.handle = src.dataset.handle || '';
          wrapper.setAttribute('data-ss-injected', '');

          // Move the inner product card content
          while (src.firstChild) wrapper.appendChild(src.firstChild);

          existingIds.add(pid);

          const meta = parseItemMeta(wrapper);
          meta.el = null;
          results.push(meta);
        });
      } catch { /* silent */ }
    });
    await Promise.all(fetches);
    return results;
  }

  // ═══════════════════════════════════════════════════════
  // §10  Browsing-Based Recommendations (kept from v0)
  // ═══════════════════════════════════════════════════════

  async function initRecommendations() {
    const recoSection = document.querySelector('[data-reco-section]');
    const recoGrid = document.querySelector('[data-reco-grid]');
    if (!recoSection || !recoGrid) return;

    const signals = getSignals();
    const history = signals.viewed_products || [];
    if (history.length === 0) return;

    try {
      const res = await fetch(`/recommendations/products.json?product_id=${history[0]}&limit=6&intent=related`);
      if (!res.ok) return;
      const data = await res.json();
      const products = data.products || [];
      if (products.length === 0) return;

      recoGrid.innerHTML = products.map(p => {
        const img = p.featured_image || p.images?.[0];
        const imgSrc = img ? (typeof img === 'string' ? img : img.src || img) : '';
        const responsiveImgHtml = imgSrc ? buildResponsiveImg(imgSrc, p.title) : '';
        return `
          <div class="productgrid-item" data-reco-item>
            <a href="${p.url}" class="zh-reco-card">
              <div class="zh-reco-img">
                ${responsiveImgHtml}
              </div>
              <div class="zh-reco-info">
                <p class="zh-reco-title">${p.title}</p>
                ${p.vendor ? `<p class="zh-reco-vendor">${p.vendor}</p>` : ''}
              </div>
            </a>
          </div>
        `;
      }).join('');
      activateAjaxImages(recoGrid);
      forceLoadVisibleImages(recoGrid);
      rescueStuckImages(recoGrid);
      recoSection.hidden = false;
    } catch { /* silent */ }
  }

  // ═══════════════════════════════════════════════════════
  // §11  Init
  // ═══════════════════════════════════════════════════════

  // §11b — homepage quickshop fallback is handled by inline <script> in zoro-home-catalog.liquid

  function init() {
    trackProductView();
    trackPageView();
    initHomepageShuffle();
    initCollectionShuffle();
    initRecommendations();
    // homepage quickshop fallback is handled by inline script in zoro-home-catalog.liquid
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
